import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const SecurityActivityLog = ({ timeRange }) => {
  const [filterType, setFilterType] = useState('all');

  const activities = [
    {
      id: 'LOG-001',
      type: 'authentication',
      severity: 'info',
      icon: 'LogIn',
      title: 'Successful Admin Login',
      description: 'User sarah.johnson@example.com logged in successfully',
      user: 'Sarah Johnson',
      ipAddress: '192.168.1.100',
      location: 'New York, US',
      timestamp: '2 mins ago',
      device: 'Chrome on Windows'
    },
    {
      id: 'LOG-002',
      type: 'security_alert',
      severity: 'high',
      icon: 'ShieldAlert',
      title: 'Multiple Failed Login Attempts',
      description: '5 failed login attempts detected from suspicious IP',
      user: 'Unknown',
      ipAddress: '185.220.101.55',
      location: 'Unknown',
      timestamp: '15 mins ago',
      device: 'Automated Script'
    },
    {
      id: 'LOG-003',
      type: 'configuration',
      severity: 'medium',
      icon: 'Settings',
      title: 'Security Policy Updated',
      description: 'Password policy strength requirements modified',
      user: 'Michael Chen',
      ipAddress: '192.168.1.105',
      location: 'Los Angeles, US',
      timestamp: '1 hour ago',
      device: 'Chrome on MacOS'
    },
    {
      id: 'LOG-004',
      type: 'api_access',
      severity: 'warning',
      icon: 'Code',
      title: 'API Rate Limit Approached',
      description: 'Client API-KEY-789 approaching rate limit threshold',
      user: 'Integration Service',
      ipAddress: '10.0.0.45',
      location: 'AWS us-east-1',
      timestamp: '2 hours ago',
      device: 'API Client v2.1'
    },
    {
      id: 'LOG-005',
      type: 'permission',
      severity: 'info',
      icon: 'UserCog',
      title: 'User Role Modified',
      description: 'User role changed from Agent to Operations Manager',
      user: 'Sarah Johnson',
      ipAddress: '192.168.1.100',
      location: 'New York, US',
      timestamp: '3 hours ago',
      device: 'Chrome on Windows'
    },
    {
      id: 'LOG-006',
      type: 'data_access',
      severity: 'info',
      icon: 'Database',
      title: 'Sensitive Data Accessed',
      description: 'Customer PII data viewed for compliance review',
      user: 'Michael Chen',
      ipAddress: '192.168.1.105',
      location: 'Los Angeles, US',
      timestamp: '4 hours ago',
      device: 'Chrome on MacOS'
    },
    {
      id: 'LOG-007',
      type: 'security_alert',
      severity: 'medium',
      icon: 'AlertTriangle',
      title: 'Unusual Login Location',
      description: 'Login from new geographic location detected',
      user: 'david.wilson@example.com',
      ipAddress: '103.28.42.156',
      location: 'Singapore',
      timestamp: '5 hours ago',
      device: 'Safari on iPhone'
    }
  ];

  const filterTypes = [
    { value: 'all', label: 'All Activities', count: activities?.length },
    { value: 'authentication', label: 'Authentication', count: activities?.filter(a => a?.type === 'authentication')?.length },
    { value: 'security_alert', label: 'Security Alerts', count: activities?.filter(a => a?.type === 'security_alert')?.length },
    { value: 'configuration', label: 'Configuration', count: activities?.filter(a => a?.type === 'configuration')?.length },
    { value: 'api_access', label: 'API Access', count: activities?.filter(a => a?.type === 'api_access')?.length }
  ];

  const filteredActivities = filterType === 'all' 
    ? activities 
    : activities?.filter(a => a?.type === filterType);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-error/10 text-error border-error/20';
      case 'medium': return 'bg-warning/10 text-warning border-warning/20';
      case 'low': return 'bg-accent/10 text-accent border-accent/20';
      case 'info': return 'bg-primary/10 text-primary border-primary/20';
      default: return 'bg-muted/50 text-muted-foreground border-border';
    }
  };

  const getSeverityBadgeColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-error/10 text-error';
      case 'medium': return 'bg-warning/10 text-warning';
      case 'low': return 'bg-accent/10 text-accent';
      case 'info': return 'bg-primary/10 text-primary';
      default: return 'bg-muted/50 text-muted-foreground';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <Icon name="ScrollText" size={20} />
              <span>Security Activity Log</span>
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Comprehensive audit trail of security events
            </p>
          </div>
          <button className="px-4 py-2 text-sm font-medium text-primary hover:text-primary/80 flex items-center space-x-2">
            <Icon name="Download" size={16} />
            <span>Export Log</span>
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex border-b border-border overflow-x-auto">
        {filterTypes?.map((filter) => (
          <button
            key={filter?.value}
            onClick={() => setFilterType(filter?.value)}
            className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap
              ${filterType === filter?.value
                ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
          >
            <span>{filter?.label}</span>
            <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs rounded-full
              ${filterType === filter?.value ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
              {filter?.count}
            </span>
          </button>
        ))}
      </div>

      {/* Activity Log */}
      <div className="p-4 space-y-3 max-h-[600px] overflow-y-auto">
        {filteredActivities?.map((activity) => (
          <div 
            key={activity?.id} 
            className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${getSeverityColor(activity?.severity)}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start space-x-3 flex-1">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getSeverityBadgeColor(activity?.severity)}`}>
                  <Icon name={activity?.icon} size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-medium text-foreground">{activity?.title}</h4>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getSeverityBadgeColor(activity?.severity)}`}>
                      {activity?.severity?.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    {activity?.description}
                  </p>
                  <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center space-x-1">
                      <Icon name="User" size={12} />
                      <span>{activity?.user}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="MapPin" size={12} />
                      <span>{activity?.location}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="Globe" size={12} />
                      <span>{activity?.ipAddress}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="Monitor" size={12} />
                      <span>{activity?.device}</span>
                    </span>
                  </div>
                </div>
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap ml-4">
                {activity?.timestamp}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SecurityActivityLog;